import React, { useEffect, useRef, useState } from 'react';
import { fetchVerifiedSnapEngageData } from 'hello-react/web-app-client/namespace/help';
import { useWebAppClient } from 'hello-react/web-app-client';
import ReactDOM from 'react-dom';

export type IframeProps = {
  src: string
  className?: string
  isActive: boolean,
  scaled: boolean,
}

export type VerifyData = {
  signature: string,
  nonce: string,
  email: string,
  ts: number,
  chatId: string,
  scaled: boolean,
}

export default function SnapEngageIframe(props: IframeProps) {
  const snapEngageIframe = useRef<HTMLIFrameElement>(null);
  const [shouldReturnNoIframe, setShouldReturnNoIframe] = useState(false);
  const [verifiedUserData, setVerifiedUserData] = useState({});
  const [startLiveChatOnVerifiedUserData, setStartLiveChatOnVerifiedUserData] = useState(false);
  const { help } = useWebAppClient();
  const portal = document.createElement('div');

  useEffect(() => {
    document.body.appendChild(portal);
    return () => { document.body.removeChild(portal); };
  }, [portal]);

  useEffect(() => {
    const verifyData: () => void = async () => {
      let returnedVerifiedUserData :VerifyData;
      try {
        returnedVerifiedUserData = await help.fetchVerifiedSnapEngageData(props.scaled);
        setVerifiedUserData(returnedVerifiedUserData);
      } catch {
        returnedVerifiedUserData = await fetchVerifiedSnapEngageData(props.scaled);
        setVerifiedUserData(returnedVerifiedUserData);
      }

      if (Object.keys(returnedVerifiedUserData).length === 0) {
        setShouldReturnNoIframe(true);
      } else {
        setShouldReturnNoIframe(false);
      }
    };
    if (props.isActive) {
      verifyData();
    }
  }, [help, props.scaled, props.isActive]);

  useEffect(() => {
    const isVerifiedUserDataEmpty = Object.keys(verifiedUserData).length === 0;
    const iframe = snapEngageIframe.current;

    const handleIframeMessage = (event: any) => {
      switch (event.data) {
        case 'opened':
          if (iframe) {
            iframe.style.width = '400px';
            iframe.style.height = '530px';
          }
          break;
        case 'minimized':
        default:
          if (iframe) {
            iframe.style.width = '85px';
            iframe.style.height = '85px';
          }
          break;
      }
    };

    // Listen for messages from the iframe
    window.addEventListener('message', handleIframeMessage);

    if (iframe) {
      // Wait for iframe to load
      const onLoad = () => {
        const contentWindow = iframe.contentWindow;
        if (contentWindow
          && props.isActive
          && !isVerifiedUserDataEmpty) {
          contentWindow.postMessage({
            message: 'startLiveChat',
            verifiedUserData,
          }, iframe.src);
          setStartLiveChatOnVerifiedUserData(false);
        } else {
          setStartLiveChatOnVerifiedUserData(true);
        }
      };

      if (!isVerifiedUserDataEmpty && startLiveChatOnVerifiedUserData) {
        onLoad();
      }

      iframe.addEventListener('load', onLoad);
      iframe.addEventListener('message', handleIframeMessage);

      return () => {
        iframe.removeEventListener('load', onLoad);
        iframe.removeEventListener('message', handleIframeMessage);
      };
    }

    return () => {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isActive, verifiedUserData]);

  if (shouldReturnNoIframe || !props.isActive) {
    return <div data-testid='se-div'></div>;
  }

  return ReactDOM.createPortal(
    <iframe
      id="snapengage-iframe"
      ref={snapEngageIframe}
      className={props.className}
      src={props.src}
      data-testid="se-iframe" />,
    portal,
  );
}
